import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';

import { SnAlarm } from '@lib/api/entities';
import { t } from '@lib/translation';

import { AlprDetectionService, AlprDetails } from '../../../services/alpr/alpr-detection.service';

@Component({
  selector: 'fs-license-plate-details',
  templateUrl: './license-plate-details.component.html',
  styleUrls: ['./license-plate-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LicensePlateDetailsComponent implements OnInit, OnChanges {
  @Input()
  public alarm: SnAlarm;

  @Output()
  public searchPlate = new EventEmitter<string>();

  @Output()
  public copyPlate = new EventEmitter<string>();

  // Memoized ALPR details to avoid repeated service calls
  private _alprDetails: AlprDetails | null = null;

  constructor(
    private readonly _alprDetection: AlprDetectionService,
    private readonly _router: Router,
    private readonly _cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Component initialization
    this._updateAlprDetails();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['alarm'] && !changes['alarm'].firstChange) {
      this._updateAlprDetails();
      this._cdr.markForCheck();
    }
  }

  /**
   * Updates the memoized ALPR details when alarm changes
   */
  private _updateAlprDetails(): void {
    if (this.alarm) {
      this._alprDetails = this._alprDetection.getAlprDetails(this.alarm);
    } else {
      this._alprDetails = null;
    }
  }

  /**
   * Gets the memoized ALPR details
   */
  private get alprDetails(): AlprDetails | null {
    return this._alprDetails;
  }

  get plateNumber(): string {
    return this.alprDetails?.plate || '';
  }

  get plateState(): string {
    return this.alprDetails?.state?.toString() || t('alpr.fallbacks.notSpecified');
  }

  get hasImage(): boolean {
    return !!(this.plateImageUrl || this.alarm?.recordingUrl);
  }

  get plateImageUrl(): string | undefined {
    return this.alprDetails?.plateImage || undefined;
  }

  get providerName(): string {
    return this._alprDetection.getProviderDisplayName(this.alarm);
  }

  getAlertReason(): string {
    return (
      this.alprDetails?.reason || this.alarm?.details?.reason || t('alpr.licensePlate.unknownAlert')
    );
  }

  public onSearchPlate(plateNumber?: string): void {
    const plate = plateNumber || this.plateNumber;
    if (plate && plate !== '— — — — — — —') {
      // Open LPR search in new tab with alert context for 30-day search
      const url = this._router.serializeUrl(
        this._router.createUrlTree(['/plates/list/search'], {
          queryParams: {
            plate,
            fromAlert: 'true',
            alertId: this.alarm?.id,
            searchReason: 'Alert Investigation',
          },
        }),
      );
      window.open(url, '_blank');
    }
  }

  public onCopyPlate(plateNumber?: string): void {
    const plate = plateNumber || this.plateNumber;
    if (plate && plate !== '— — — — — — —') {
      navigator.clipboard?.writeText(plate);
      this.copyPlate.emit(plate);
    }
  }

  public searchCurrentPlate(): void {
    this.onSearchPlate(this.plateNumber);
  }

  public copyCurrentPlate(): void {
    this.onCopyPlate(this.plateNumber);
  }
}
